import { InvitationCollectionDeleteItemsFilters, InvitationCollectionItem, InvitationCollectionQueryFilters } from '../types/invitation-tracker.type'
import { canRemoveInvitationCollectionItem } from './invitation-action-taken.util'

export type InvitationCollectionSelectionMode = 'none' | 'include' | 'all' | 'exclude'

export type InvitationCollectionSelectionState = {
  mode: InvitationCollectionSelectionMode
  includedIds: Set<number>
  excludedIds: Set<number>
}

/**
 * Returns a fresh empty selection state.
 */
export function createEmptyInvitationCollectionSelection(): InvitationCollectionSelectionState {
  return {
    mode: 'none',
    includedIds: new Set(),
    excludedIds: new Set()
  }
}

/**
 * Returns how many collection items are currently selected.
 */
export function getInvitationCollectionSelectionCount(state: InvitationCollectionSelectionState, collectionTotal: number | undefined): number {
  switch (state.mode) {
    case 'none':
      return 0
    case 'include':
      return state.includedIds.size
    case 'all':
      return collectionTotal ?? 0
    case 'exclude':
      return Math.max((collectionTotal ?? 0) - state.excludedIds.size, 0)
    default:
      return 0
  }
}

/**
 * Returns true when the entire collection is selected with no exclusions.
 */
export function isAllInvitationCollectionSelected(state: InvitationCollectionSelectionState): boolean {
  return state.mode === 'all' && state.excludedIds.size === 0
}

/**
 * Returns true when a collection item is part of the current selection.
 */
export function isInvitationCollectionItemSelected(state: InvitationCollectionSelectionState, itemId: number): boolean {
  switch (state.mode) {
    case 'none':
      return false
    case 'include':
      return state.includedIds.has(itemId)
    case 'all':
    case 'exclude':
      return !state.excludedIds.has(itemId)
    default:
      return false
  }
}

/**
 * Resolves checkbox keys for loaded rows from the selection state.
 */
export function getInvitationCollectionSelectedKeys(state: InvitationCollectionSelectionState, loadedItems: InvitationCollectionItem[]): string[] {
  return loadedItems.filter((item) => isInvitationCollectionItemSelected(state, item.id)).map((item) => String(item.id))
}

/**
 * Applies select-all or clear-all for the full collection.
 */
export function setInvitationCollectionSelectAll(selected: boolean): InvitationCollectionSelectionState {
  if (!selected) {
    return createEmptyInvitationCollectionSelection()
  }

  return {
    mode: 'all',
    includedIds: new Set(),
    excludedIds: new Set()
  }
}

/**
 * Toggles a single item in the current selection mode.
 */
export function toggleInvitationCollectionItemSelection(
  state: InvitationCollectionSelectionState,
  itemId: number,
  collectionTotal: number | undefined
): InvitationCollectionSelectionState {
  const isSelected = isInvitationCollectionItemSelected(state, itemId)

  if (state.mode === 'all') {
    if (!isSelected) return state

    const excludedIds = new Set([itemId])
    if (collectionTotal != null && excludedIds.size >= collectionTotal) {
      return createEmptyInvitationCollectionSelection()
    }

    return { mode: 'exclude', includedIds: new Set(), excludedIds }
  }

  if (state.mode === 'exclude') {
    const excludedIds = new Set(state.excludedIds)

    if (isSelected) {
      excludedIds.add(itemId)
      if (collectionTotal != null && excludedIds.size >= collectionTotal) {
        return createEmptyInvitationCollectionSelection()
      }

      return { mode: 'exclude', includedIds: new Set(), excludedIds }
    }

    excludedIds.delete(itemId)
    if (excludedIds.size === 0) {
      return { mode: 'all', includedIds: new Set(), excludedIds: new Set() }
    }

    return { mode: 'exclude', includedIds: new Set(), excludedIds }
  }

  if (state.mode === 'include') {
    const includedIds = new Set(state.includedIds)

    if (isSelected) {
      includedIds.delete(itemId)
      if (includedIds.size === 0) {
        return createEmptyInvitationCollectionSelection()
      }

      return { mode: 'include', includedIds, excludedIds: new Set() }
    }

    includedIds.add(itemId)
    return { mode: 'include', includedIds, excludedIds: new Set() }
  }

  return {
    mode: 'include',
    includedIds: new Set([itemId]),
    excludedIds: new Set()
  }
}

/**
 * Reconciles desktop table key changes into include/all/exclude selection state.
 */
export function reconcileInvitationCollectionSelectionFromLoadedKeys(
  previous: InvitationCollectionSelectionState,
  newKeys: Set<string>,
  loadedItems: InvitationCollectionItem[],
  collectionTotal: number | undefined
): InvitationCollectionSelectionState {
  const loadedIds = loadedItems.map((item) => item.id)

  if (previous.mode === 'all' || previous.mode === 'exclude') {
    const excludedIds = new Set(previous.excludedIds)

    for (const id of loadedIds) {
      if (newKeys.has(String(id))) {
        excludedIds.delete(id)
      } else {
        excludedIds.add(id)
      }
    }

    if (excludedIds.size === 0) {
      return { mode: 'all', includedIds: new Set(), excludedIds: new Set() }
    }

    if (collectionTotal != null && excludedIds.size >= collectionTotal) {
      return createEmptyInvitationCollectionSelection()
    }

    return { mode: 'exclude', includedIds: new Set(), excludedIds }
  }

  if (newKeys.size === 0) {
    return createEmptyInvitationCollectionSelection()
  }

  return {
    mode: 'include',
    includedIds: new Set(Array.from(newKeys, Number)),
    excludedIds: new Set()
  }
}

/**
 * Removes a deleted item from the current selection state.
 */
export function removeInvitationCollectionItemFromSelection(state: InvitationCollectionSelectionState, itemId: number): InvitationCollectionSelectionState {
  if (state.mode === 'include') {
    const includedIds = new Set(state.includedIds)
    if (!includedIds.has(itemId)) return state

    includedIds.delete(itemId)
    if (includedIds.size === 0) {
      return createEmptyInvitationCollectionSelection()
    }

    return { mode: 'include', includedIds, excludedIds: new Set() }
  }

  if (state.mode === 'exclude') {
    const excludedIds = new Set(state.excludedIds)
    if (!excludedIds.has(itemId)) return state

    excludedIds.delete(itemId)
    if (excludedIds.size === 0) {
      return { mode: 'all', includedIds: new Set(), excludedIds: new Set() }
    }

    return { mode: 'exclude', includedIds: new Set(), excludedIds }
  }

  return state
}

/**
 * Builds the API selection payload for delete-items preview.
 */
export function buildInvitationCollectionDeleteItemsSelectionPayload(state: InvitationCollectionSelectionState): {
  all: boolean
  ids?: number[]
  excluded_ids?: number[]
} {
  switch (state.mode) {
    case 'all':
      return { all: true }
    case 'exclude':
      return { all: true, excluded_ids: Array.from(state.excludedIds) }
    case 'include':
      return { all: false, ids: Array.from(state.includedIds) }
    default:
      return { all: false, ids: [] }
  }
}

/**
 * Builds optional bulk-action filters from the active list query (search + collection filters).
 * When select-all is used on a filtered list, the API needs these filters to scope `all: true`.
 */
export function buildInvitationCollectionDeleteItemsFilters(
  searchTerm: string,
  queryFilters: InvitationCollectionQueryFilters = {}
): InvitationCollectionDeleteItemsFilters | undefined {
  const search = searchTerm.trim()
  const filters: InvitationCollectionDeleteItemsFilters = search ? { ...queryFilters, search } : { ...queryFilters }

  return Object.keys(filters).length ? filters : undefined
}

/**
 * Returns true when at least one collection entry is selected (navbar bulk remove action).
 */
export function canShowInvitationCollectionBulkRemove(
  state: InvitationCollectionSelectionState,
  collectionTotal: number | undefined,
  _loadedItems: InvitationCollectionItem[]
): boolean {
  return getInvitationCollectionSelectionCount(state, collectionTotal) > 0
}

/**
 * Returns true when every selected entry is known to be non-removable (already invited).
 * Returns false when any selected entry can be removed, or when removability cannot be determined yet.
 */
export function areAllSelectedInvitationCollectionItemsNonRemovable(
  state: InvitationCollectionSelectionState,
  collectionTotal: number | undefined,
  loadedItems: InvitationCollectionItem[]
): boolean {
  const selectedItems = getKnownSelectedInvitationCollectionItems(state, collectionTotal, loadedItems)
  if (!selectedItems?.length) return false
  return selectedItems.every((item) => !canRemoveInvitationCollectionItem(item))
}

/**
 * Collects selected collection items that are already loaded. Returns null when the full
 * selection cannot be resolved from loaded rows yet (e.g. select-all with unloaded pages).
 */
export function getKnownSelectedInvitationCollectionItems(
  state: InvitationCollectionSelectionState,
  collectionTotal: number | undefined,
  loadedItems: InvitationCollectionItem[]
): InvitationCollectionItem[] | null {
  const selectionCount = getInvitationCollectionSelectionCount(state, collectionTotal)
  if (selectionCount === 0) return []

  if (state.mode === 'include') {
    const loadedById = new Map(loadedItems.map((item) => [item.id, item]))
    const items: InvitationCollectionItem[] = []

    for (const id of state.includedIds) {
      const item = loadedById.get(id)
      if (!item) return null
      items.push(item)
    }

    return items
  }

  const allLoaded = collectionTotal != null && loadedItems.length >= collectionTotal
  if (!allLoaded) return null

  const selectedItems = state.mode === 'exclude' ? loadedItems.filter((item) => !state.excludedIds.has(item.id)) : loadedItems
  return selectedItems.length ? selectedItems : []
}
